/**
 * O que faz: informa como uma pessoa pode solicitar a exclusão de dados no Gens CRM.
 * Onde usa: rota pública /exclusao-de-dados e campo de instruções de exclusão no app Meta.
 * Conexões: React Router e componentes visuais reutilizáveis; não consulta serviços externos.
 * Tabelas: nenhuma.
 * Data: 26/09/2026.
 */
import { ArrowLeft, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export const DataDeletionInstructionsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto max-w-4xl px-4">
        <div className="mb-8">
          <Link
            to="/signup"
            className="mb-4 inline-flex items-center gap-2 font-medium text-blue-600 hover:text-blue-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar ao cadastro
          </Link>

          <div className="mb-2 flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-2">
              <Trash2 className="h-6 w-6 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Solicitação de exclusão de dados</h1>
          </div>
          <p className="text-gray-600">Última atualização: 26/09/2026</p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl">Como solicitar a exclusão</CardTitle>
          </CardHeader>
          <CardContent className="prose max-w-none space-y-6">
            <p className="text-gray-700">
              Para solicitar a exclusão de dados pessoais tratados pelo Gens CRM, envie um e-mail para{' '}
              <a className="text-blue-600 underline" href="mailto:contato@genscrm.com.br?subject=Solicita%C3%A7%C3%A3o%20de%20exclus%C3%A3o%20de%20dados">
                contato@genscrm.com.br
              </a>.
            </p>

            <section>
              <h2 className="text-lg font-semibold text-gray-900">Informações necessárias</h2>
              <p className="text-gray-700">Para localizar a solicitação com segurança, informe:</p>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>seu nome;</li>
                <li>o e-mail ou telefone usado no relacionamento com a clínica;</li>
                <li>o nome da clínica, se conhecido; e</li>
                <li>uma descrição do dado ou conta a que a solicitação se refere.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900">Análise da solicitação</h2>
              <p className="text-gray-700">
                Confirmaremos a identidade da pessoa solicitante antes de excluir informações, para evitar que
                dados sejam removidos por terceiros. Alguns registros podem precisar ser mantidos quando a
                legislação exigir sua conservação.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900">Integrações Meta e WhatsApp</h2>
              <p className="text-gray-700">
                Se a solicitação envolver dados recebidos por uma integração Meta ou WhatsApp, a análise
                considerará também os registros necessários para segurança, auditoria e cumprimento das
                políticas aplicáveis. A confirmação do atendimento será enviada pelo mesmo canal de contato.
              </p>
            </section>

            <p className="text-gray-700">
              Saiba mais sobre o tratamento de informações em nossa{' '}
              <Link className="text-blue-600 underline" to="/politica-de-privacidade">Política de Privacidade</Link>.
            </p>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <Button asChild className="bg-blue-600 hover:bg-blue-700">
            <Link to="/signup">Voltar ao cadastro</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DataDeletionInstructionsPage;
